import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  usePandadocSync,
  useTriggerFakturowniaGroupSync,
  useTriggerZoomGroupProcess,
} from 'core/hooks';

import * as L from '../../_detailShared/detail.styled';
import { GroupColors } from './groupColors';

interface ISyncMenuProps {
  groupId: string;
  /** Called after a sync finishes — caller refetches GroupDetailQuery. */
  onSynced: () => void;
}

const Wrap = styled.div`
  position: relative;
  display: inline-block;
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 50;
  min-width: 260px;
  background: ${GroupColors.surface};
  border: 1px solid ${GroupColors.border};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  padding: 6px;
`;

const Item = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  font: inherit;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 500;
  color: ${GroupColors.textPrimary};
  border-radius: 6px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${GroupColors.surfaceMuted};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const ItemHint = styled.span`
  color: ${GroupColors.textMuted};
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

/**
 * Dropdown next to the HeaderBar primary actions. Now group-scoped
 * (backend TZ §G9 + §G10 shipped):
 *   - Fakturownia → `triggerFakturowniaGroupSync(groupId)` — narrows
 *     the queue to this group's payer emails.
 *   - Zoom → `triggerZoomGroupProcess(groupId)` — re-runs auto-match
 *     + recording sync for the group's lessons in the default window.
 *   - PandaDoc stays global (the `usePandadocSync` hook owns its own
 *     status poll); the page refetches after it drains so the
 *     PandaDoc tab sees fresh data.
 */
export const SyncMenu = memo(({ groupId, onSynced }: ISyncMenuProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const fakturownia = useTriggerFakturowniaGroupSync(groupId, onSynced);
  const zoom = useTriggerZoomGroupProcess(groupId, onSynced);
  const pandadoc = usePandadocSync(onSynced);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const onFakturownia = useCallback(() => {
    setOpen(false);
    fakturownia.trigger().catch(() => undefined);
  }, [fakturownia]);

  const onZoom = useCallback(() => {
    setOpen(false);
    zoom.trigger().catch(() => undefined);
  }, [zoom]);

  const onPandadoc = useCallback(() => {
    setOpen(false);
    pandadoc.triggerSync();
  }, [pandadoc]);

  const anySyncing = fakturownia.pending || zoom.pending || pandadoc.syncing;

  return (
    <Wrap ref={wrapRef}>
      <L.PrimaryActionButton
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={anySyncing}
      >
        {anySyncing
          ? t('pages.groupDetail.sync.syncing')
          : t('pages.groupDetail.sync.menu')}
        {' ▾'}
      </L.PrimaryActionButton>
      {open ? (
        <Menu>
          <Item
            type="button"
            onClick={onFakturownia}
            disabled={fakturownia.pending}
          >
            <span>{t('pages.groupDetail.sync.fakturownia')}</span>
            <ItemHint>
              {fakturownia.pending
                ? t('pages.groupDetail.sync.running')
                : t('pages.groupDetail.sync.scoped')}
            </ItemHint>
          </Item>
          <Item type="button" onClick={onZoom} disabled={zoom.pending}>
            <span>{t('pages.groupDetail.sync.zoom')}</span>
            <ItemHint>
              {zoom.pending
                ? t('pages.groupDetail.sync.running')
                : t('pages.groupDetail.sync.scoped')}
            </ItemHint>
          </Item>
          <Item type="button" onClick={onPandadoc} disabled={pandadoc.syncing}>
            <span>{t('pages.groupDetail.sync.pandadoc')}</span>
            <ItemHint>
              {pandadoc.syncing
                ? t('pages.groupDetail.sync.running')
                : t('pages.groupDetail.sync.global')}
            </ItemHint>
          </Item>
        </Menu>
      ) : null}
    </Wrap>
  );
});
